/**
 * Invoice API Contract Schemas
 *
 * Defines Zod schemas for invoice-related API requests and responses.
 * Used for validation in both backend (NestJS) and frontend/mobile clients.
 */

import { z } from 'zod';

// ============================================
// ENUMS
// ============================================

export const InvoiceStatusEnum = z.enum(['DRAFT', 'ISSUED', 'PAID', 'CANCELLED']);
export type InvoiceStatusValue = z.infer<typeof InvoiceStatusEnum>;

export const CommissionInvoiceStatusEnum = z.enum(['ISSUED', 'PAID', 'OVERDUE', 'CANCELLED']);
export type CommissionInvoiceStatusValue = z.infer<typeof CommissionInvoiceStatusEnum>;

// ============================================
// INVOICE SCHEMAS
// ============================================

export const InvoiceLineItemSchema = z.object({
  description: z.string(),
  hsnSac: z.string().optional(),
  quantity: z.number(),
  unitPrice: z.number(),
  amount: z.number(),
  gstRate: z.number().optional(),
  gstAmount: z.number().optional(),
});
export type InvoiceLineItem = z.infer<typeof InvoiceLineItemSchema>;

export const InvoiceSchema = z.object({
  id: z.string().uuid(),
  invoiceNumber: z.string(),
  userId: z.string().uuid(),
  bookingId: z.string().uuid(),
  paymentId: z.string().uuid().nullable().optional(),
  status: InvoiceStatusEnum,
  subtotal: z.number(),
  taxAmount: z.number(),
  totalAmount: z.number(),
  taxType: z.enum(['CGST_SGST', 'IGST']).optional(), // Tax type for Indian GST
  // GST breakdown fields (for GSTR-1 filing)
  cgstAmount: z.number(),
  sgstAmount: z.number(),
  igstAmount: z.number(),
  placeOfSupply: z.string().nullable().optional(),
  invoiceType: z.string().nullable().optional(),
  customerName: z.string().nullable().optional(),
  // Discount Fields
  grossAmount: z.number(),
  discountAmount: z.number(),
  couponCode: z.string().nullable().optional(),
  couponDiscount: z.number().optional(),
  foodDiscount: z.number().optional(),
  walletCreditsUsed: z.number().optional(),
  netTaxableAmount: z.number(),
  // Commission Fields
  commissionPercentage: z.number().optional(),
  commissionAmount: z.number().optional(),
  commissionGst: z.number().optional(),
  netPayoutToVendor: z.number().optional(),
  // Vendor Business Fields (for tax invoice display)
  vendorGstin: z.string().nullable().optional(),
  vendorPan: z.string().nullable().optional(),
  vendorBusinessName: z.string().optional(),
  vendorAddress: z.string().optional(),
  vendorPhone: z.string().nullable().optional(),
  lineItems: z.array(InvoiceLineItemSchema),
  issuedAt: z.string(),
  dueDate: z.string().nullable().optional(),
  paidAt: z.string().nullable().optional(),
  createdAt: z.string(),
  booking: z
    .object({
      id: z.string().uuid(),
      venue: z.object({
        id: z.string().uuid(),
        name: z.string(),
      }),
      bookingDate: z.string(),
    })
    .optional(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

export const InvoiceListResponseSchema = z.object({
  data: z.array(InvoiceSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type InvoiceListResponse = z.infer<typeof InvoiceListResponseSchema>;

// ============================================
// QUERY SCHEMAS
// ============================================

export const InvoiceFilterSchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
  status: InvoiceStatusEnum.optional(),
  fromDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  toDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
export type InvoiceFilterQuery = z.infer<typeof InvoiceFilterSchema>;

// ============================================
// INVOICE GENERATION SCHEMAS
// ============================================

/**
 * Schema for checking invoice eligibility for a booking
 * Used by GET /invoices/check-eligibility/:bookingId
 */
export const InvoiceEligibilitySchema = z.object({
  bookingId: z.string().uuid(),
  eligible: z.boolean(),
  prerequisites: z.object({
    paymentExists: z.boolean(),
    paymentCompleted: z.boolean(),
    bookingFinished: z.boolean().optional(),
    invoiceExists: z.boolean(),
  }),
  reason: z.string().nullable(),
  invoiceId: z.string().uuid().nullable(),
});
export type InvoiceEligibility = z.infer<typeof InvoiceEligibilitySchema>;

/**
 * Schema for manual invoice generation request
 * Used by POST /invoices/generate-for-booking/:bookingId
 */
export const InvoiceGenerateRequestSchema = z.object({
  bookingId: z.string().uuid(),
});
export type InvoiceGenerateRequest = z.infer<typeof InvoiceGenerateRequestSchema>;

/**
 * Schema for manual invoice generation response
 * Returns either the generated invoice or error details
 */
export const InvoiceGenerateResponseSchema = z.discriminatedUnion('success', [
  z.object({
    success: z.literal(true),
    invoice: InvoiceSchema,
  }),
  z.object({
    success: z.literal(false),
    error: z.string(),
    prerequisites: z.object({
      paymentExists: z.boolean(),
      paymentCompleted: z.boolean(),
      bookingFinished: z.boolean(),
      invoiceExists: z.boolean(),
    }),
  }),
]);
export type InvoiceGenerateResponse = z.infer<typeof InvoiceGenerateResponseSchema>;

// ============================================
// COMMISSION INVOICE SCHEMAS
// ============================================

/**
 * Filter/query schema for listing commission invoices
 * Used by GET /commission-invoices
 */
export const CommissionInvoiceFilterSchema = z.object({
  page: z.coerce.number().min(1).default(1).optional(),
  limit: z.coerce.number().min(1).max(100).default(20).optional(),
  fromDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  toDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  status: CommissionInvoiceStatusEnum.optional(),
});
export type CommissionInvoiceFilterQuery = z.infer<typeof CommissionInvoiceFilterSchema>;

/**
 * Response schema for a single commission invoice
 * Matches CommissionInvoiceResponseDto from backend
 */
export const CommissionInvoiceResponseSchema = z.object({
  id: z.string().uuid(),
  invoiceNumber: z.string(), // COMM/2024-25/000001
  financialYear: z.string(), // "2024-25"
  sequenceNumber: z.number(),

  // Vendor details
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  vendorGstin: z.string().nullable(),
  vendorAddress: z.string().nullable(),

  // Booking references
  bookingIds: z.array(z.string().uuid()),
  periodStart: z.string(), // ISO date string
  periodEnd: z.string(), // ISO date string

  // Commission calculation
  totalBookingAmount: z.number(),
  commissionRate: z.number(), // Percentage e.g. 5.0
  commissionAmount: z.number(), // Amount before GST

  // GST breakdown
  cgstAmount: z.number(),
  sgstAmount: z.number(),
  igstAmount: z.number(),
  totalGst: z.number(),
  totalAmount: z.number(), // Commission + GST

  // Compliance
  placeOfSupply: z.string(), // State code
  sacCode: z.string(), // "998599"
  invoiceType: z.string(), // "TAX_INVOICE" | "BILL_OF_SUPPLY"

  // Payment tracking
  issuedAt: z.string(), // ISO date string
  dueDate: z.string().nullable(),
  paidAt: z.string().nullable(),
  status: CommissionInvoiceStatusEnum,

  // Deduction tracking
  deductedFromPayout: z.boolean(),
  payoutId: z.string().nullable(),

  // TDS fields (Section 194O)
  tdsRate: z.number(),
  tdsAmount: z.number(),
  tdsSection: z.string(),

  // TCS fields (CGST Section 52)
  tcsRate: z.number(),
  tcsAmount: z.number(),

  // Net amount vendor receives after all deductions
  netPayoutAmount: z.number().nullable(),

  createdAt: z.string(), // ISO date string
  updatedAt: z.string(), // ISO date string
});
export type CommissionInvoiceResponse = z.infer<typeof CommissionInvoiceResponseSchema>;

/**
 * Paginated list response for commission invoices
 * Used by GET /commission-invoices (list endpoint)
 */
export const CommissionInvoiceListResponseSchema = z.object({
  data: z.array(CommissionInvoiceResponseSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type CommissionInvoiceListResponse = z.infer<typeof CommissionInvoiceListResponseSchema>;
